import React, { useEffect, useState } from "react";

import { css } from "@compiled/react";
import { FormattedMessage } from "react-intl";

import { Field } from "@atlaskit/form";
import { RadioGroup } from "@atlaskit/radio";
import { OptionsPropType } from "@atlaskit/radio/types";

export type SiteSelectorRadioGroupProps = {
    readonly sites: string[];

    readonly onSiteChanged: () => void;
};

export type SiteSelectorRadioGroupState = {
    fieldName: string;
    radioOptions: OptionsPropType;
    selectedSite: string | number | undefined;
};

const SiteSelectorRadioGroup = ({
    sites,
    onSiteChanged
}: SiteSelectorRadioGroupProps) => {
    const [state, setState] = useState<SiteSelectorRadioGroupState>({
        fieldName: "",
        radioOptions: [],
        selectedSite: ""
    });
    const { fieldName, radioOptions, selectedSite } = state;

    useEffect(() => {
        const fieldName = "site";
        const radioOptions = getRadioOptions(sites, fieldName);
        const defaultValue = radioOptions[0]?.value;

        setState({
            fieldName,
            radioOptions,
            selectedSite: defaultValue
        });
    }, [sites]);

    const getRadioOptions = (sites: string[], fieldName: string) => {
        const radioOptions: OptionsPropType = sites.map((site) => {
            // HOT-101715 fixes
            const { hostname: originalHostname, searchParams } = new URL(site);
            let hostname = originalHostname;
            try {
                const continueVal = searchParams.get("continue");
                hostname = continueVal
                    ? new URL(continueVal).hostname
                    : originalHostname;
            } catch {}
            return {
                name: fieldName,
                value: site,
                label: hostname
            };
        });

        // Include option to "Start a new site"
        radioOptions.push({
            name: fieldName,
            value: "",
            label: (
                <FormattedMessage
                    id="SiteSelectorModal.startASite"
                    defaultMessage="Start a new site"
                />
            )
        });

        return radioOptions;
    };

    const onValueChange: React.ChangeEventHandler<HTMLInputElement> = (
        event: React.ChangeEvent
    ) => {
        onSiteChanged();

        // In our custom onChange handler, we need to manage the RadioGroup state in our component's state
        const newValue = event.currentTarget.getAttribute("value");
        const newSiteSelected = newValue ? newValue : undefined;

        setState({ ...state, selectedSite: newSiteSelected });
    };

    return (
        <Field
            name={fieldName}
            label={
                <FormattedMessage
                    id="SiteSelectorModal.selectASite"
                    defaultMessage="Select a site"
                />
            }
            defaultValue={selectedSite}
        >
            {({ fieldProps }: { fieldProps: object }) => (
                <div css={radioGroupStyles}>
                    <RadioGroup
                        {...fieldProps}
                        options={radioOptions}
                        onChange={onValueChange}
                    />
                </div>
            )}
        </Field>
    );
};

const radioGroupStyles = css({
    maxHeight: "calc(100vh - 422px)",
    overflowY: "auto"
});

export default SiteSelectorRadioGroup;
